import {WidgetFullEditorPortal} from "../common/WidgetFullEditorPortal"
import {ShapeWidgetData} from "@dv5/domain"
import {FC} from "react"
import {useWidgetEditState} from "../../hooks/useWidgetEditState"
import {useWidgetEditor} from "../../hooks/useWidgetEditor"
import {EditorWidgetProps} from "../../model/WidgetProps"
import {widgetService} from "../../services/widgetService"
import {WidgetPlainText} from "../common/WidgetPlainText"
import {WidgetFrame} from "../common/WidgetFrame"
import {shapeComponents} from "./shapes"
import {ShapeWidgetFullEditor} from "./ShapeWidgetFullEditor"
import {ShapeWidgetInlineEditor} from "./ShapeWidgetInlineEditor"
import {ShapeWidgetToolbar} from "./ShapeWidgetToolbar"
import './ShapeWidget.less'

type Props = EditorWidgetProps

export const ShapeWidget: FC<Props> = ({
                                           canvasId,
                                           widget,
                                           selected,
                                           settings,
                                           editingWidgetId,
                                           onStartDrag,
                                           onStartResize,
                                           onContextMenu,
                                           dragState,
                                           onStartLinkCreation,
                                           onCompleteLinkCreation,
                                           onNavigate,
                                           onDeleteDataLink,
                                           onStopEditing,
                                       }) => {
    const data = widget.data as ShapeWidgetData
    const ShapeComponent = shapeComponents[data.shapeType] ?? shapeComponents.RECTANGLE
    const {isFullEditor, setIsFullEditor, stopEditing} = useWidgetEditState(onStopEditing, settings.readOnly)

    const {
        containerRef,
        resizeCursor,
        handlers,
        renderResizeHandles,
        renderLinkDots,
    } = useWidgetEditor({
        widget,
        selected,
        editingWidgetId,
        dragState,
        onStartDrag,
        onStartResize,
        onContextMenu,
        onStartLinkCreation,
        onCompleteLinkCreation,
        onNavigate,
        onDeleteDataLink,
    })

    if (editingWidgetId && editingWidgetId === widget.id) {
        const toolbarStyles = widgetService.getToolbarStyle(widget)
        return (
            <div className='shape-edit-container' ref={containerRef} onContextMenu={handlers.onContextMenu}>
                {!isFullEditor && (
                    <ShapeWidgetToolbar
                        canvasId={canvasId}
                        widgetId={widget.id}
                        data={data}
                        toolbarStyles={toolbarStyles}
                        onExpandToFull={() => setIsFullEditor(true)}
                    />
                )}
                <div
                    className={widgetService.getWidgetClasses(widget, 'shapeWidget', selected, editingWidgetId, widget.commentsCount > 0)}
                    style={widgetService.getWidgetStyle(widget)}
                >
                    <svg width='100%' height='100%' viewBox={`0 0 ${widget.width} ${widget.height}`} preserveAspectRatio='none'>
                        <ShapeComponent data={data} width={widget.width} height={widget.height} />
                    </svg>
                    <div className='shape-widget__text' style={{color: data.fgColor || '#333333'}}>
                        <ShapeWidgetInlineEditor
                            canvasId={canvasId}
                            widgetId={widget.id}
                            data={data}
                            text={data.text || ''}
                            containerRef={containerRef}
                        />
                    </div>
                </div>
                {isFullEditor && (
                    <WidgetFullEditorPortal>
                        <ShapeWidgetFullEditor
                            canvasId={canvasId}
                            widgetId={widget.id}
                            data={data}
                            toolbarStyles={toolbarStyles}
                            onClose={stopEditing}
                        />
                    </WidgetFullEditorPortal>
                )}
            </div>
        )
    }

    return (
        <WidgetFrame
            baseClass='shapeWidget'
            canvasId={canvasId}
            widget={widget}
            settings={settings}
            selected={selected}
            editingWidgetId={editingWidgetId}
            resizeCursor={resizeCursor}
            handlers={handlers}
            containerRef={containerRef}
            renderResizeHandles={renderResizeHandles}
            renderLinkDots={renderLinkDots}
            onNavigate={onNavigate}
            onDeleteDataLink={onDeleteDataLink}
        >
            <svg width='100%' height='100%' viewBox={`0 0 ${widget.width} ${widget.height}`} preserveAspectRatio='none'>
                <ShapeComponent data={data} width={widget.width} height={widget.height} />
            </svg>
            {data.text && (
                <div className='shape-widget__text' style={{color: data.fgColor || '#333333'}}>
                    <WidgetPlainText text={data.text} />
                </div>
            )}
        </WidgetFrame>
    )
}
